import { createContext, useContext, useState } from 'react';
import { cn } from '@/lib/utils';

export type Segmento = 'PJ' | 'PF';

/**
 * ⛔ CPF E CNPJ NÃO SE MISTURAM NA FILA (Douglas 17/09/2026).
 *
 * É a REGRA GLOBAL do CRM chegando na esteira: PJ e PF nunca se somam em venda,
 * meta nem comissão — e não podem se somar na tela de quem trabalha a fila.
 * A triagem continua UMA (a regra de entrada de cada segmento é a de hoje, não
 * mudou nada), mas ela mostra **um segmento por vez**.
 *
 * ⛔ A separação é por FILTRO, nunca por coluna nova: nada muda de fila fora da
 * triagem, então o kanban não ganha coluna de CPF nem move card. Quem separa é
 * o seletor; as colunas ficam exatamente onde estão.
 *
 * Diferença proposital em relação ao segmentado do topo do kanban: lá "nenhum
 * ligado" mostra os dois (é panorama). Aqui não existe esse estado — a fila
 * nasce em CNPJ e o CPF é um clique. Fila de trabalho misturada é o defeito que
 * esta régua conserta.
 */
const SegmentoEsteiraContext = createContext<Segmento | undefined>(undefined);

export const SegmentoEsteiraProvider = SegmentoEsteiraContext.Provider;

/**
 * O segmento da triagem. Herda o que está ligado no topo do kanban (abriu a
 * triagem com CPF ligado, ela abre em CPF) e pode ser trocado dentro dela sem
 * mexer no kanban — um filtro só, sem a tela de fora dizendo uma coisa e a fila
 * dizendo outra. Sem nada ligado no kanban, nasce em CNPJ.
 */
export function useSegmentoTriagem() {
  const herdado = useContext(SegmentoEsteiraContext);
  const [segmento, setSegmento] = useState<Segmento>(herdado ?? 'PJ');
  return { segmento, setSegmento };
}

/**
 * `tipo_pessoa` vem da `v_planilha_mae` e é do CLIENTE, então é igual em todas
 * as linhas do pedido — vale ler a primeira. Linha sem `tipo_pessoa` conta como
 * CNPJ: é o que o CRM foi por 5 anos, e o padrão não pode ser esconder pedido.
 */
export function ehDoSegmento(tipo: string | null | undefined, segmento: Segmento) {
  return (tipo === 'PF' ? 'PF' : 'PJ') === segmento;
}

/** "CNPJ" ou "CPF" — o rótulo que o humano lê, em todo o CRM. */
export function rotuloSegmento(s: Segmento) {
  return s === 'PF' ? 'CPF' : 'CNPJ';
}

/**
 * A COR DO SEGMENTO — a diferença gravada geral no CRM (Douglas 17/09/2026).
 * Não é cor nova: é a primária de cada marca — bordô da **Empresarial Prime**
 * no CNPJ, rosé da **Prime Conecta** no CPF. Tokens no `index.css`, bloco
 * SEGMENTO, e expostos no tailwind como `segmento-pj` / `segmento-pf`.
 *
 * ⛔ Segue o REGISTRO, nunca quem está logado: o mesmo BKO atende as duas
 * marcas na mesma fila, no mesmo dia.
 */
export const CLASSES_SEGMENTO = {
  PJ: { texto: 'text-segmento-pj', fundo: 'bg-segmento-pj', chapa: 'bg-segmento-pj-bg text-segmento-pj' },
  PF: { texto: 'text-segmento-pf', fundo: 'bg-segmento-pf', chapa: 'bg-segmento-pf-bg text-segmento-pf' },
} as const;

export function segmentoDe(tipo: string | null | undefined): Segmento {
  return tipo === 'PF' ? 'PF' : 'PJ';
}

/**
 * Máscara do documento pelo que ELE é, não pelo nome da coluna: 11 dígitos =
 * CPF, 14 = CNPJ. Antes as triagens só sabiam mascarar 14 e o pedido de CPF
 * aparecia como "—" na coluna CNPJ — documento que existe e a tela não mostra
 * (Douglas 17/09/2026: "veja para incluir então dados, que é CNPJ ou CPF").
 */
export function formatarDocumento(doc: string | null | undefined): string {
  if (!doc) return '-';
  if (doc.includes('.')) return doc;
  const d = doc.replace(/\D/g, '');
  if (d.length === 11) return d.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  if (d.length === 14) return d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  return doc;
}
